import { RadioGroupInput, Typography, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AdminJobPositionNode,
  AuthAccountOrganizationJobPositionStatusChoices,
  UpdateJobPositionStatusMutation,
  UpdateJobPositionStatusMutationVariables,
  useUpdateJobPositionStatusMutation,
} from "@graphql/generated";
import { Button, Modal, ModalBody, ModalContent, ModalProps } from "@heroui/react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

export type ChangeStatusModalProps = Pick<ModalProps, "isOpen" | "onClose" | "onOpenChange"> & {
  refreshData?: () => void;
  status?: AuthAccountOrganizationJobPositionStatusChoices;
  jobPosition?: AdminJobPositionNode;
};

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  refreshData,
  status,
  jobPosition,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<AuthAccountOrganizationJobPositionStatusChoices | undefined>(
    status,
  );

  const { id } = useParams();
  const [updateJobPositionStatus, { loading }] = useUpdateJobPositionStatusMutation();

  const handleSelectStatus = async () => {
    if (!id || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    await handleApiRequest<UpdateJobPositionStatusMutation, UpdateJobPositionStatusMutationVariables>({
      apiFunction: updateJobPositionStatus,
      variables: {
        id,
        status: selectedStatus,
      },
      componentName: "ChangeStatusModal",
      actionName: "updateJobPositionStatus",
      onSuccess: () => {
        refreshData?.();
        onClose?.();
      },
      successMessage: "Job post status updated successfully",
      fallbackErrorMessage: "Failed to update job post status",
      extraErrorContext: {
        jobPostId: id,
        newStatus: selectedStatus,
      },
    });
  };

  useEffect(() => {
    if (!status) return;
    setSelectedStatus(status);
  }, [status]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} onOpenChange={onOpenChange} size="2xl">
      <ModalContent className="p-4">
        <ModalBody className="flex items-center gap-8">
          <RadioGroupInput
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e?.target?.value as AuthAccountOrganizationJobPositionStatusChoices);
            }}
            classNames={{
              label: "text-lg text-black",
            }}
            orientation="vertical"
            label={"Which status do you want to change the job status to?"}
            items={[
              {
                label: <CustomLabel title="Open" description="The recruitment process is open and ongoing." />,
                value: AuthAccountOrganizationJobPositionStatusChoices.PUBLISHED,
              },
              {
                label: (
                  <CustomLabel
                    title="Suspended"
                    description="The recruitment process is temporarily suspended and closed  for some reason."
                  />
                ),
                value: AuthAccountOrganizationJobPositionStatusChoices.SUSPENDED,
              },
              {
                label: <CustomLabel title="Rejected" description="The recruitment process is rejected." />,
                value: AuthAccountOrganizationJobPositionStatusChoices.REJECTED,
                disabled: !jobPosition?.isEditable,
              },
            ]}
          />
          <Button
            color="success"
            className="w-60"
            onClick={handleSelectStatus}
            isLoading={loading}
            isDisabled={loading}
          >
            Save
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CustomLabel = ({ title, description }: { title: string; description: string }) => {
  return (
    <div>
      <Typography className="text-black">{title}</Typography>
      <Typography variant="muted">{description}</Typography>
    </div>
  );
};
